/**
 * V2 source path: customer/midas/components/v2/MidasFleetChipsV2.tsx
 * V1 source: customer/midas/components/MidasFleetChips.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 3 — Round 17 (Alt A Calm restyle + Claim 7 row fix + diode tri-state)
 *
 * 3-line text-only machine rows. **Round 17 row restructure (Claim 7 fix):**
 *   L1 — Sora 13/500 #2A2824 — MODEL only (was: brand + model — duplicated L2)
 *   L2 — Sora 11 normal #8C8375 + JBM 11 #8C8375 mono SN — manufacturer · SN-####
 *   L3 — Sora 11/400 #8C8375 — primary · secondary location (em-dash if missing)
 *
 * 8px SR diode in the LEFT slot. **Round 17 diode tri-state (REVERSES Round 5):**
 *   - "loading"   → muted stone #D8D2C4 (no telemetry yet / loading)
 *   - "open-sr"   → terracotta  #D4572B + 4px LED glow (has open SR)
 *   - "healthy"   → calm green  #2D8C4E + 4px LED glow (no open SR, fleet ready)
 *
 * Active row marker: 3px terracotta left rule + warm-cream #F4EFE3 fill (Alt A).
 *
 * Truncation: single-line ellipsis on L1 + native `title` attribute as fallback
 * tooltip — touch users still see the truncated label, AT users get the full
 * aria-label (model + manufacturer + SN) for screen-reader continuity.
 *
 * Search: filter applied in parent (MidasSidebarV2) via `searchTerm` prop.
 * Match is case-insensitive substring against model + serialNumber +
 * manufacturer name. Memoized — recomputes only when term or fleet changes.
 *
 * Logic copied from V1 MidasFleetChips.tsx:
 *   - Redux selectors: machinesMap, machineryLoading, machineContext.
 *   - Redux dispatches via the parent-supplied onMachineClick handler.
 *   - getMachines() lazy-fetch on mount when fleet is empty.
 */
import React, { useEffect, useMemo } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { Machine } from "../../../machinery/models/models";

interface MidasFleetChipsV2Props {
  onMachineClick: (machine: Machine) => void;
  /** Called AFTER onMachineClick runs — used by phone overlay to dismiss the drawer. */
  onAfterClick?: () => void;
  /** Search term applied case-insensitively against model + SN + manufacturer. */
  searchTerm?: string;
  /** Whether the fleet is being fetched for the first time — drives diode "loading" state. */
  fleetLoading?: boolean;
}

type DiodeState = "loading" | "open-sr" | "healthy";

/**
 * V2 inline diode color helper. Round 17 tri-state — reverses Round 5's binary:
 *   activeServiceRequestCount > 0 → "open-sr"  (terracotta)
 *   activeServiceRequestCount === 0 + fleet loaded → "healthy" (calm green)
 *   fleet still loading / count undefined → "loading" (muted stone)
 */
const getDiodeState = (machine: Machine, isLoading: boolean): DiodeState => {
  if (isLoading) return "loading";
  if (typeof machine.activeServiceRequestCount !== "number") return "loading";
  return machine.activeServiceRequestCount > 0 ? "open-sr" : "healthy";
};

/** Format the location line per Round 5 spec — em-dash fallback keeps row heights uniform. */
const formatLocation = (machine: Machine): string => {
  const primary = machine.locationName?.trim() || "";
  const secondary = machine.parentLocationName?.trim() || "";
  if (primary && secondary) return `${primary} · ${secondary}`;
  if (primary) return primary;
  if (secondary) return secondary;
  return "—";
};

const SidebarRowSkeleton: React.FC = () => (
  <div className="flex min-h-[64px] items-start gap-2.5 rounded-md px-3 py-2.5">
    <div className="mt-1.5 h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-midas-stone" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 w-24 animate-pulse rounded bg-midas-stone" />
      <div className="h-2.5 w-32 animate-pulse rounded bg-midas-stone/70" />
      <div className="h-2.5 w-28 animate-pulse rounded bg-midas-stone/50" />
    </div>
  </div>
);

const MidasFleetChipsV2: React.FC<MidasFleetChipsV2Props> = ({
  onMachineClick,
  onAfterClick,
  searchTerm = "",
  fleetLoading = false,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) => translate(k as any, { defaultValue: f }) as string;

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);

  // Flatten machines from grouped map (V1 shape)
  const allMachines = useMemo(() => {
    const flat: Machine[] = [];
    if (machinesMap && typeof machinesMap === "object") {
      Object.values(machinesMap).forEach((group) => {
        if (Array.isArray(group)) flat.push(...(group as Machine[]));
      });
    }
    return flat;
  }, [machinesMap]);

  // Round 17: client-side substring filter — case-insensitive match against
  // model + serialNumber + manufacturer (custom brand or canonical name).
  const filteredMachines = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allMachines;
    return allMachines.filter((m) => {
      const model = (m.model || "").toLowerCase();
      const sn = (m.serialNumber || "").toLowerCase();
      const brand = (m.customBrandName || m.manufacturer?.name || "").toLowerCase();
      return model.includes(q) || sn.includes(q) || brand.includes(q);
    });
  }, [allMachines, searchTerm]);

  // V1 logic — fetch on mount when empty
  useEffect(() => {
    if (allMachines.length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
  }, [allMachines.length, machineryLoading, dispatch]);

  // Treat the union of "fleet still arriving" + "explicit prop" as loading
  const showLoading = (machineryLoading || fleetLoading) && allMachines.length === 0;

  if (showLoading) {
    return (
      <div
        id="midas-sidebar-fleet-panel"
        role="tabpanel"
        aria-label={t("midas.v2.fleet", "Fleet")}
        className="flex flex-col gap-1 px-2 pb-3"
      >
        {[0, 1, 2, 3].map((i) => (
          <SidebarRowSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (allMachines.length === 0) {
    return (
      <div
        id="midas-sidebar-fleet-panel"
        role="tabpanel"
        aria-label={t("midas.v2.fleet", "Fleet")}
        className="px-3 py-6 text-center"
      >
        <p
          className="font-sora text-[12px] text-midas-muted-stone"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {t("midas.v2.noMachines", "No machines yet.")}
        </p>
      </div>
    );
  }

  if (filteredMachines.length === 0) {
    return (
      <div
        id="midas-sidebar-fleet-panel"
        role="tabpanel"
        aria-label={t("midas.v2.fleet", "Fleet")}
        className="px-3 py-6 text-center"
      >
        <p
          className="font-sora text-[12px] text-midas-muted-stone"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {translate("midas.v2.noMachineMatches", {
            query: searchTerm.trim(),
            defaultValue: `No machines match "${searchTerm.trim()}".`,
          })}
        </p>
      </div>
    );
  }

  return (
    <ul
      id="midas-sidebar-fleet-panel"
      role="tabpanel"
      aria-label={t("midas.v2.fleet", "Fleet")}
      className="flex flex-col gap-0.5 px-2 pb-3"
    >
      {filteredMachines.map((machine) => {
        const isActive = machineContext?.machineId === machine.id;
        const diode = getDiodeState(machine, machineryLoading);
        const displayName = machine.model?.trim() || "Unknown";
        const brandName =
          machine.customBrandName || machine.manufacturer?.name || "";

        // Claim 7 fix — line 1 = MODEL only. Manufacturer + SN moves to line 2.
        const line1 = displayName;

        // aria-label keeps the full identifier for screen-reader continuity
        const ariaFullName = brandName
          ? `${brandName} ${displayName}`.trim()
          : displayName;

        // Title attribute as a touch/keyboard tooltip fallback for ellipsised L1
        const titleAttr = ariaFullName;

        const diodeStyle =
          diode === "open-sr"
            ? { backgroundColor: "#D4572B", boxShadow: "0 0 4px rgba(212,87,43,0.6)" }
            : diode === "healthy"
            ? { backgroundColor: "#2D8C4E", boxShadow: "0 0 4px rgba(45,140,78,0.5)" }
            : { backgroundColor: "#D8D2C4" };

        return (
          <li key={machine.id}>
            <button
              type="button"
              onClick={() => {
                onMachineClick(machine);
                if (onAfterClick) onAfterClick();
              }}
              aria-pressed={isActive}
              aria-label={`Machine ${ariaFullName}${
                machine.serialNumber ? ` SN ${machine.serialNumber}` : ""
              }${diode === "open-sr" ? " — has open service report" : ""}`}
              title={titleAttr}
              className={`group flex min-h-[64px] w-full items-start gap-2.5 rounded-lg py-3 pr-3 text-left outline-none transition-colors duration-150 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-midas-terracotta active:scale-[0.99] motion-reduce:active:scale-100 ${
                isActive
                  ? "bg-[#F4EFE3]"
                  : "bg-transparent hover:bg-[rgba(44,38,32,0.04)]"
              }`}
              style={{
                borderLeft: `3px solid ${
                  isActive ? "#D4572B" : "transparent"
                }`,
                paddingLeft: "9px", // 3px border + 6px = aligns with 3px-rule rows
              }}
            >
              {/* SR diode — left slot, fixed width for vertical lane */}
              <span
                aria-hidden="true"
                className="mt-1.5 flex h-2 w-2 flex-shrink-0 items-center justify-center"
              >
                <span
                  className="block h-2 w-2 rounded-full"
                  style={diodeStyle}
                />
              </span>
              {/* 3-line text stack */}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {/* Line 1: model only — Sora 13.5/medium (semibold when active) */}
                <span
                  className={`truncate font-sora text-[13.5px] ${
                    isActive ? "font-semibold" : "font-medium"
                  } leading-[18px] tracking-[-0.01em] text-midas-graphite`}
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {line1}
                </span>
                {/* Line 2: manufacturer · SN-#### — Sora normal + JBM mono SN */}
                {(brandName || machine.serialNumber) && (
                  <span
                    className="truncate text-[10px] leading-3 text-midas-muted-stone"
                  >
                    {brandName && (
                      <span
                        className="font-sora"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                      >
                        {brandName}
                      </span>
                    )}
                    {brandName && machine.serialNumber && (
                      <span
                        className="font-sora"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                      >
                        {" · "}
                      </span>
                    )}
                    {machine.serialNumber && (
                      <span
                        className="font-mono"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        SN-{machine.serialNumber}
                      </span>
                    )}
                  </span>
                )}
                {/* Line 3: location */}
                <span
                  className="truncate font-sora text-[11px] font-normal leading-[14px] text-midas-muted-stone"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {formatLocation(machine)}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default MidasFleetChipsV2;
